import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, Clock, ArrowRight, Calendar, User, Tag, ArrowLeft } from "lucide-react";
import HeroBanner from "../components/HeroBanner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [config, setConfig] = useState(null);
  const [activeCategory, setActiveCategory] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch(`${API_URL}/api/blog`).then(res => res.json()),
      fetch(`${API_URL}/api/config`).then(res => res.json())
    ])
      .then(([postsData, configData]) => {
        setPosts(postsData);
        setConfig(configData);
        setLoading(false);
      })
      .catch(console.error);
  }, []);

  const categories = [
    { id: "all", label: "Tous les articles" },
    { id: "conseils", label: "Conseils" },
    { id: "securite", label: "Sécurité" },
    { id: "guide", label: "Guides" },
    { id: "urgence", label: "Urgences" }
  ];

  const filteredPosts = activeCategory === "all" 
    ? posts 
    : posts.filter(post => post.category === activeCategory);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div data-testid="blog-page">
      {/* Hero */}
      <HeroBanner
        title="Blog Fichet Dépannage"
        subtitle="Conseils, guides et actualités sur la serrurerie Fichet. Apprenez à entretenir vos serrures et à sécuriser votre domicile."
        highlightText="Fichet"
        showFeatures={false}
        showCTA={false}
        config={config}
        variant="compact"
        breadcrumb={
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Accueil
          </Link>
        }
      />

      {/* Category filters */}
      <section className="py-6 bg-white border-b border-gray-100 sticky top-16 md:top-20 z-40">
        <div className="container-custom">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeCategory === cat.id
                    ? "bg-orange-500 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
                data-testid={`blog-category-${cat.id}`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="section">
        <div className="container-custom">
          {filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 mb-4">
                Aucun article trouvé dans cette catégorie.
              </p>
              <button
                onClick={() => setActiveCategory("all")}
                className="text-orange-600 font-medium hover:underline"
              >
                Voir tous les articles
              </button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredPosts.map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="group bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all border border-gray-100"
                  data-testid={`blog-post-${post.slug}`}
                >
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={post.image_url}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-orange-500 text-white text-xs font-medium rounded-full capitalize">
                        {post.category}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h2 className="text-xl font-bold text-[#0f2744] mb-3 group-hover:text-orange-600 transition-colors line-clamp-2">
                      {post.title}
                    </h2>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                      {post.excerpt}
                    </p>

                    {/* Meta */}
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <div className="flex items-center gap-4">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(post.published_at).toLocaleDateString('fr-FR', { 
                            day: 'numeric', 
                            month: 'short', 
                            year: 'numeric' 
                          })}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {post.read_time} min
                        </span>
                      </div>
                      <span className="flex items-center gap-1 text-orange-600 font-medium group-hover:gap-2 transition-all">
                        Lire
                        <ArrowRight className="w-3 h-3" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-[#0f2744] to-[#1a3a5c] py-12 text-white">
        <div className="container-custom text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Besoin d'un serrurier Fichet ?
          </h2>
          <p className="text-gray-300 mb-6 max-w-xl mx-auto">
            Nos techniciens interviennent rapidement pour tous vos problèmes de serrurerie.
          </p>
          {config && (
            <a
              href={config.phone_link} onClick={() => { if(window.gtag_report_conversion) window.gtag_report_conversion(config.phone_link); }}
              className="inline-flex items-center gap-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-4 rounded-xl font-bold text-xl shadow-xl hover:from-orange-600 hover:to-orange-700 transition-all"
              data-testid="blog-cta-phone"
            >
              <Phone className="w-6 h-6" />
              {config.phone}
            </a>
          )}
        </div>
      </section>
    </div>
  );
}
